import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Explore Amenities CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Label', type: 'text', defaultValue: 'Explore Our Amenities' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

export function PropertyOverview() {
  const ctaLink = useSlotGroup('propertyoverview.cta_link', ctaLinkDef);

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="pt-8 md:pt-12 pb-8 md:pb-12 bg-[var(--vibe-color-secondary)]">
      <div className="max-w-5xl px-4 sm:px-6 lg:px-8 text-left">
        <div ref={headingRef} className="reveal-fade-up">
          <RichTextContent
            path="propertyoverview.pre_title"
            as="p"
            className="font-body text-sm sm:text-base text-[var(--vibe-color-text-on-secondary)] mb-6 tracking-wide uppercase"
          />

          <RichTextContent
            path="propertyoverview.title"
            as="h2"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--vibe-color-text-on-secondary)] mb-4 leading-tight uppercase"
          />

          <div className="w-20 h-1 mb-8 rounded-full bg-[var(--vibe-color-primary)]" />

          <RichTextContent
            path="propertyoverview.description"
            as="div"
            className="font-body text-sm sm:text-base text-[var(--vibe-color-text-on-secondary)] max-w-3xl leading-relaxed [&_a]:text-[var(--vibe-color-primary)] [&_a]:underline"
          />

          <EditableSlotGroup groupPath="propertyoverview.cta_link" definition={ctaLinkDef}>
            <a
              href={ctaLink.url || '/amenities'}
              target={ctaLink._target || '_self'}
              className="inline-block text-center mt-8 px-10 py-4 text-sm font-medium tracking-[0.2em] uppercase rounded-full border border-[var(--vibe-color-primary)] text-[var(--vibe-color-text-on-secondary)] hover:bg-[var(--vibe-color-primary)] hover:text-[var(--vibe-color-text-on-primary)] transition-all"
            >
              {ctaLink.label || 'Explore Our Amenities'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.pre_title': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: 'Life at our community is like nowhere else',
    },
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<span data-type="field-token" data-field="property_name"></span>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_home_amenities_blurb',
      defaultValue: '<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>',
    },
  },
  slotGroups: {
    'propertyoverview.cta_link': ctaLinkDef,
  },
};

(PropertyOverview as any).contentSchema = contentSchema;

export default PropertyOverview;
